import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BottomTabBarItem {
  key: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  onSelect: () => void;
  /** Extra data-tab attribute (the native shell's tab id). */
  dataTab?: string;
}

/**
 * The phone tab bar's look, shared by the website (BottomNav) and the Android
 * shell (mobile/TabBar), which differ only in what a tap does.
 *
 * Docked to the bottom edge (owner, 12 Oct, option 1 of three; Codex on PR
 * #1275 picked the same): the floating pill let page content show around and
 * under it and covered the last card's buttons. One card-coloured surface
 * runs through the safe area, so nothing peeks past it, and pages reserve
 * exactly its row height plus the safe area (--mobile-bottom-nav-height).
 *
 * Teal marks the selected tab (navigation); clay stays for real actions. Each
 * tab's whole quarter of the bar is its tap target, labels are 12px, and the
 * selected state is carried by the tint pill, the colour, the label weight
 * and aria-current, not colour alone.
 */
export function BottomTabBarSurface({
  items,
  className,
  testId,
}: {
  items: BottomTabBarItem[];
  className?: string;
  testId?: string;
}) {
  return (
    <nav
      className={cn('fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card shadow-[0_-4px_16px_-10px_rgba(0,0,0,0.25)]', className)}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label="Mobile navigation"
      data-testid={testId}
    >
      <div className="mx-auto flex h-16 max-w-md items-stretch">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={item.onSelect}
            className={cn(
              'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
              item.active ? 'text-trust' : 'text-muted-foreground hover:text-foreground',
            )}
            aria-label={item.label}
            aria-current={item.active ? 'page' : undefined}
            data-tab={item.dataTab}
          >
            <span
              className={cn(
                'flex h-8 w-14 items-center justify-center rounded-full transition-colors duration-200',
                item.active && 'bg-kanfy-tint-teal',
              )}
              aria-hidden="true"
            >
              <item.icon className="h-[22px] w-[22px]" strokeWidth={item.active ? 2.25 : 2} />
            </span>
            <span className={cn('max-w-full truncate text-xs leading-none', item.active ? 'font-semibold' : 'font-medium')}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
}
